/**
 * [REALTIME] NEW FILE (2026-06-23) — client side of the real-time data layer.
 * ============================================================================
 * ONE shared poller hits GET /api/data-version (tiny: a handful of integers)
 * every few seconds. When a dataset's integer changes, it (1) drops that
 * dataset's stale client-side GET cache and (2) notifies the open page so it
 * silently refetches. The whole app shares this single poll regardless of how
 * many pages/components subscribe — so it does NOT add per-component polling.
 *
 * Pauses while the tab is hidden (document.hidden) and catches up immediately on
 * focus, mirroring the existing per-page polling guards.
 *
 * Additive + safe: if the poll fails (network blip, old server without the
 * endpoint) it is swallowed and the existing per-page polling / on-write bus
 * keep the app working exactly as before.
 */
import { fetchDataVersions, invalidateGetCacheByPrefix } from './api';

// dataset key (must match the server's dataVersion.ts) → the API URL prefixes
// whose cached responses are now stale and must be dropped.
// [PROCESS-ANALYSIS 2026-07-31] '/process-analysis' is listed under inventory, payments AND
// eom because the tab aggregates all of them (client revenue, PM retention, the month-end
// close). Without these entries a live refetch would be pointless: the page would re-request
// the endpoint, the axios GET cache would answer from its own 5-minute copy, and the tab
// would keep showing the figures it loaded with.
const DATASET_PREFIXES: Record<string, string[]> = {
  inventory:       ['/inventory', '/process-analysis', '/pm-recommendations'], // [PM-RECS 2026-08-25]
  // [PAID-GREEN 2026-10-07] '/sales-pipeline': a card turns green when its website's New Sale payment lands.
  payments:        ['/payments', '/process-analysis', '/pm-recommendations', '/sales-pipeline'],
  retention:       ['/retention-pipeline'],
  eom:             ['/eom', '/eom-table', '/process-analysis'],
  teamPerformance: ['/team-performance'],
  delayed:         ['/delayed-payments'],
  newSales:        ['/new-sales'],
  // [MYSALES-FRESH 2026-09-18, user] My Sales was the one money tab NOT on this poller, so an open
  // tab kept its own copy for up to 5 minutes and a new deal seemed to take for ever to show.
  mySales:         ['/my-sales'],
  // [SALES-PIPELINE 2026-10-07] Bumped by the server's own write path (dataVersion bumpAppDataset).
  salesPipeline:   ['/sales-pipeline'],
  // [LEAD-ASSIGNMENT 2026-10-13] The same: a Leads / Not Leads click anywhere refreshes every open Lead Assignment tab.
  leadAssignment:  ['/lead-assignment'],
  // [LGP 2026-10-15] A Lead Generator Performance roster edit / Add member / bonus save: its own tab + Team Sameer Report
  // (which reads the roster's team and ROW Status).
  leadGenPerf:     ['/lead-generator-performance', '/team-sameer-report'],
  // [LAST-ACTIVITY 2026-10-16, user] Bumped by the server each time someone's last activity is stamped (at most once a
  // minute per person, only while they use the app): User Management's Last activity column updates without a reload.
  users:           ['/users'],
  // [NET-IN-BANK NEW SALES 2026-09-16] New Sales' summary now carries Sales refunds/disputes, so a
  // refund/dispute change must drop its cached copy too (the table rows are unaffected).
  refunds:         ['/refunds', '/new-sales/summary'],
  disputes:        ['/disputes', '/new-sales/summary'],
  // [ACCEPT-REFRESH 2026-09-17, user] CLIENT-LOCAL key: the server never reports an
  // `activityLogs` version, so the poll never fires it. notifyDataChanged() below does, right
  // after a write this tab made, so an open Application Logs page reloads and a cached copy drops.
  activityLogs:    ['/activity-logs'],
};

type Listener = () => void;
const listeners = new Map<string, Set<Listener>>(); // dataset → subscribers
let lastVersions: Record<string, number> = {};
let timer: ReturnType<typeof setInterval> | null = null;
let started = false;
// [WRITE-LATENCY 2026-08-25] 4 s → 2 s, paired with the server watcher's 2 s tick: a change
// made elsewhere (or finished after a save's reply) now reaches an open tab in ≤4 s, not ≤8 s.
// The poll is one tiny GET of a few integers; a hidden tab still does not poll at all.
const DEFAULT_POLL_MS = 2000;

async function poll(): Promise<void> {
  // Don't poll a backgrounded tab — resumes on visibilitychange below.
  if (typeof document !== 'undefined' && document.hidden) return;
  let versions: Record<string, number>;
  try {
    ({ versions } = await fetchDataVersions());
  } catch {
    return; // network blip / not logged in — existing polling is the fallback
  }

  const changed: string[] = [];
  for (const ds of Object.keys(versions)) {
    if (lastVersions[ds] !== undefined && versions[ds] !== lastVersions[ds]) changed.push(ds);
  }
  lastVersions = versions;
  applyChanges(changed);
}

// [ACCEPT-REFRESH 2026-09-17, user] The poll's "drop the cache + tell the open pages" step, pulled
// out unchanged so notifyDataChanged() can run exactly the same thing for a write this tab made.
function applyChanges(changed: string[]): void {
  if (!changed.length) return;

  // 1) Drop stale cache for the changed datasets so refetches hit fresh data.
  invalidateGetCacheByPrefix(changed.flatMap((ds) => DATASET_PREFIXES[ds] || []));

  // 2) Notify each changed dataset's subscribers (a listener subscribed to
  //    several changed datasets fires once).
  const fired = new Set<Listener>();
  for (const ds of changed) {
    const set = listeners.get(ds);
    if (!set) continue;
    for (const fn of set) {
      if (fired.has(fn)) continue;
      fired.add(fn);
      try { fn(); } catch { /* a throwing listener must not break the others */ }
    }
  }
}

/**
 * [ACCEPT-REFRESH 2026-09-17, user] Treat `datasets` as changed NOW, without waiting for the poll:
 * drops their cached GETs and runs their subscribers (e.g. the Refunds / Disputes table's silent
 * reload). For a write whose change the server watcher cannot see — refunds/disputes are
 * fingerprinted by COUNT + MAX(id) + MAX(updated_at), and a Team / Sales-PM Name UPDATE moves
 * none of them (updated_at has no ON UPDATE), so no version ever bumps for it.
 */
export function notifyDataChanged(datasets: string[]): void {
  applyChanges(datasets);
}

/** Start the single shared poller. Idempotent — safe to call on every mount. */
export function startRealtime(pollMs = DEFAULT_POLL_MS): void {
  if (started) return;
  started = true;
  // Prime the baseline first so the first poll doesn't fire a spurious refetch.
  fetchDataVersions().then((v) => { lastVersions = v.versions; }).catch(() => {});
  timer = setInterval(() => { void poll(); }, pollMs);
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => { if (!document.hidden) void poll(); });
  }
}

/** Stop the poller (not normally needed; the shell lives for the whole session). */
export function stopRealtime(): void {
  if (timer) { clearInterval(timer); timer = null; }
  started = false;
}

/**
 * Subscribe to changes for one or more datasets. `cb` runs (silently) whenever
 * any listed dataset's server-side version changes. Returns an unsubscribe fn.
 */
export function subscribeToData(datasets: string[], cb: Listener): () => void {
  for (const ds of datasets) {
    if (!listeners.has(ds)) listeners.set(ds, new Set());
    listeners.get(ds)!.add(cb);
  }
  return () => { for (const ds of datasets) listeners.get(ds)?.delete(cb); };
}
